import { useCallback, useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { Alert, Box, CircularProgress, Fade } from '@mui/material';
import { apiCall, ENDPOINTS } from '../utils/api.js';
import { useAppConfig } from '../utils/useAppConfig.js';

// REACT_APP_* is baked in at BUILD time, so after a Stripe test -> live swap it's easy to ship a
// live backend alongside a bundle still carrying a test key. Stripe.js then refuses to mount
// embedded checkout and the customer just sees an empty box with no error anywhere — which is
// exactly how this surfaced in production. Detect it and say so instead.
const PUBLISHABLE_KEY = (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').trim();
const KEY_MODE = PUBLISHABLE_KEY.startsWith('pk_live')
  ? 'live'
  : (PUBLISHABLE_KEY.startsWith('pk_test') ? 'test' : null);

const stripePromise = PUBLISHABLE_KEY ? loadStripe(PUBLISHABLE_KEY) : null;

/**
 * Mounts Stripe's embedded Checkout for the selected package.
 *
 * Stripe owns the whole payment step here: it collects the card, applies the free
 * trial (when the package has trial_days > 0), creates the Subscription, and
 * charges either immediately (trial_days === 0) or when the trial ends. We only
 * fetch the client_secret and mount it.
 *
 * On completion Stripe redirects to the session's return_url, which carries
 * ?session_id=... back to RegisterPage — see the return-page effect there. Account
 * state itself is set by the checkout.session.completed webhook, which is
 * authoritative; the return page is only for what the customer sees.
 *
 * Two modes:
 *   Onboarding — pass `reference` + `packageName` (+ optional `address`); the component fetches the
 *     client_secret from the onboarding checkout endpoint itself.
 *   Billing    — pass `clientSecret` directly (the caller already created the session, e.g. the
 *     billing page's Subscribe action); the component just mounts it.
 *
 * Props:
 *   clientSecret - a pre-created session client_secret (billing mode). When set, no fetch happens.
 *   reference    - the onboarding reference (from the URL) (onboarding mode)
 *   packageName  - the selected Package's display name, e.g. "Growth" (onboarding mode)
 *   address      - { line1, line2?, city, state, postal_code?, country } (onboarding mode)
 *   onError      - optional; called with a message when the session can't be created
 */
export default function StripePaymentForm({ clientSecret: providedSecret, reference, packageName, address, onError }) {
  // The server reports which Stripe mode it's in (never any key material) so a build-time key
  // mismatch can be named rather than failing silently.
  const { stripe_mode: serverMode } = useAppConfig();
  const [clientSecret, setClientSecret] = useState(providedSecret || null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(!providedSecret);

  // Serialized so a changed address refetches without re-running on every render.
  const addressKey = address ? JSON.stringify(address) : '';

  // Adjust state during render when props change (official React pattern).
  const [prevProps, setPrevProps] = useState({ reference, packageName, addressKey, providedSecret });
  if (
    prevProps.reference !== reference ||
    prevProps.packageName !== packageName ||
    prevProps.addressKey !== addressKey ||
    prevProps.providedSecret !== providedSecret
  ) {
    setPrevProps({ reference, packageName, addressKey, providedSecret });
    setLoading(!providedSecret);
    setLoadError('');
    setClientSecret(providedSecret || null);
  }

  useEffect(() => {
    // Billing mode: the client_secret is supplied, nothing to fetch.
    if (providedSecret) return undefined;
    if (!reference || !packageName) return undefined;
    const controller = new AbortController();

    apiCall(ENDPOINTS.ONBOARDING_CHECKOUT_PAY(reference), {
      method: 'POST',
      body: JSON.stringify({ package: packageName, address: address || undefined }),
      signal: controller.signal,
    })
      .then((data) => {
        if (data?.status !== 'Success' || !data.client_secret) {
          throw new Error(data?.message || 'Could not start checkout.');
        }
        setClientSecret(data.client_secret);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          const msg = err.message || 'Could not start checkout.';
          setLoadError(msg);
          setLoading(false);
          onError?.(msg);
        }
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference, packageName, addressKey, providedSecret]);

  // EmbeddedCheckoutProvider expects a function returning a Promise<clientSecret>.
  const fetchClientSecret = useCallback(() => Promise.resolve(clientSecret), [clientSecret]);
  const options = useMemo(
    () => (clientSecret ? { fetchClientSecret } : null),
    [clientSecret, fetchClientSecret],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2.5 }}>
        {loadError}
      </Alert>
    );
  }

  if (!options) return null;

  // Configuration problems that would otherwise render as a silent blank box.
  if (!PUBLISHABLE_KEY) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2.5 }}>
        Payments aren&apos;t configured for this site: the Stripe publishable key is missing.
        It is baked in at build time, so set <strong>REACT_APP_STRIPE_PUBLISHABLE_KEY</strong>
        {' '}and <strong>redeploy the frontend</strong>.
      </Alert>
    );
  }
  if (serverMode && KEY_MODE && serverMode !== KEY_MODE) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2.5 }}>
        Stripe key mismatch: this site was built with a <strong>{KEY_MODE}</strong> publishable
        key but the server is in <strong>{serverMode}</strong> mode, so checkout can&apos;t load.
        Rebuild the frontend with the matching key.
      </Alert>
    );
  }

  return (
    <Fade in timeout={400}>
      {/* Checkout renders in an iframe with no external padding; the wrapper
          supplies the margin and must not itself be inside another iframe. */}
      <Box sx={{ minHeight: 320 }}>
        <EmbeddedCheckoutProvider
          key={clientSecret}
          stripe={stripePromise}
          options={options}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </Box>
    </Fade>
  );
}
